import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useCurrentUser } from '@/hooks';

import { ArtifactSurvey } from './ArtifactSurvey';
import { MemberReflectionArtifactView } from './MemberReflectionArtifactView';
import { reflectionNotePlacement } from './reflectionNotePlacement';

import type {
  MemberReflectionArtifactHighlight,
  MemberReflectionCopyState,
  MemberReflectionFeedbackValue,
  MemberReflectionStatus,
} from './MemberReflectionArtifactView';
import type { ArtifactTextSelection } from './useArtifactTextSelection';
import type {
  MomentsArtifactDetail,
  MomentsArtifactNote,
  MomentsArtifactSurveyQuestionId,
} from '@/store/api/momentsApi';

import { copyMarkdownToClipboard } from '@/components/Markdown';
import {
  CHEM_CALL_REFLECTION_MEMBER_PROFILE_SLUG,
  MEMBER_REFLECTION_PROFILE_SLUG,
  memberReflectionLabel,
} from '@/components/Sessions/memberReflectionProfiles';
import { resolveQueryAuthorityState } from '@/features/appointments/memberCoachBookingState';
import { usesMomentsScheduling } from '@/features/appointments/schedulingProvider';
import ReflectionV2Branch, {
  MEMBER_REFLECTION_V2_PROFILE_SLUG,
} from '@/features/mcpMember/reflection/ReflectionV2Branch';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import {
  useReflectionKeepCohort,
  useReflectionKeepFlag,
} from '@/hooks/useReflectionKeepCohort';
import { buildMemberSessionTranscriptPath } from '@/pages/MemberSessionTranscript/transcriptUtils';
import {
  buildMemberMomentsAnalyticsProperties,
  MEMBER_MOMENTS_EVENTS,
  momentsAnalytics,
} from '@/services/momentsAnalytics';
import {
  MEMBER_REFLECTION_ONBOARDING_VIEW_KEY,
  useDismissDismissableViewMutation,
  useGetDismissableViewStatesQuery,
} from '@/store/api/dismissableViewsApi';
import {
  resolveMomentsBaseUrl,
  useCreateMomentsArtifactFeedbackMutation,
  useCreateMomentsArtifactNoteMutation,
  useDeleteMomentsArtifactNoteMutation,
  useGetMomentsArtifactFeedbackLatestQuery,
  useGetMomentsArtifactQuery,
  useGetMomentsArtifactSurveyStatusQuery,
  useListMomentsArtifactNotesQuery,
  useRecordMomentsArtifactSurveyAnswerMutation,
  useUpdateMomentsArtifactNoteMutation,
} from '@/store/api/momentsApi';
import {
  useGetSessionQuery,
  useGetSessionsDashboardQuery,
} from '@/store/api/sessionApi';
import { legacyStoredSourceIdentity } from '@/utils/artifactNoteIdentity';
import { formatDateLabel, toDateSlug } from '@/utils/dateSlug';
import { captureMessage } from '@/utils/errorMonitoring';

const getErrorStatus = (error: unknown): string | number | undefined => {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return undefined;
  }
  return (error as { status?: string | number }).status;
};

// The page shows one generic message for every load failure, so the log
// carries the cause: FETCH_ERROR (no response), an HTTP status, or
// MOMENTS_NOT_READY with the readiness reason.
const getLoadErrorDetails = (error: unknown) => {
  const data =
    typeof error === 'object' && error !== null && 'data' in error
      ? (error as { data?: unknown }).data
      : undefined;
  const record =
    typeof data === 'object' && data !== null
      ? (data as Record<string, unknown>)
      : undefined;
  return {
    error_code: typeof record?.error === 'string' ? record.error : undefined,
    error_message: typeof data === 'string' ? data : undefined,
    error_status: getErrorStatus(error),
    readiness_reason:
      typeof record?.reason === 'string' ? record.reason : undefined,
  };
};

// Error codes and readiness reasons also come from the response body. Send
// them to Mixpanel only when they look like a code (e.g. "invalid_grant"),
// never as free text.
const toAnalyticsCode = (value: string | undefined): string | undefined =>
  value !== undefined && /^[a-z0-9_]{1,64}$/u.test(value) ? value : undefined;

type LoadErrorDetails = ReturnType<typeof getLoadErrorDetails>;

// Moments gives no session at all to a coach-role person without Google Drive,
// including for their own member artifacts, so neither a retry nor a refresh
// helps. Connecting Drive does (MEN-5250).
const isDriveConnectRequired = (details: LoadErrorDetails): boolean =>
  details.error_status === 'MOMENTS_NOT_READY' &&
  details.readiness_reason === 'drive_connect_required';

// Shown with every failure so a screenshot or replay names the cause, using
// the same fields as the log and the Mixpanel event. Only code-shaped values
// are shown, never response text.
const getLoadErrorCode = (details: LoadErrorDetails): string | undefined => {
  const status = details.error_status;
  const displayStatus =
    typeof status === 'number' ||
    (typeof status === 'string' && /^[A-Z0-9_]{1,40}$/u.test(status))
      ? String(status)
      : undefined;
  const parts = [
    displayStatus,
    toAnalyticsCode(details.readiness_reason) ??
      toAnalyticsCode(details.error_code),
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : undefined;
};

// Only causes whose meaning is certain get a sentence. None of them tells the
// person what to do: the fix for a missing sign-in cookie is not yet known.
const getLoadErrorReason = (
  status: LoadErrorDetails['error_status'],
): string | undefined => {
  if (status === 401) {
    return "Moments couldn't confirm your sign-in.";
  }
  if (status === 'FETCH_ERROR') {
    return "We couldn't reach Moments.";
  }
  return undefined;
};

const isNotFoundOrAccessDenied = (error: unknown): boolean => {
  const status = getErrorStatus(error);
  return status === 403 || status === 404;
};

const getBackFallbackPath = (viewerContext: 'coach' | 'member') =>
  viewerContext === 'coach' ? '/coach' : '/m';

const getFeedbackSurface = (
  isSessionPrep: boolean,
  viewerContext: 'coach' | 'member',
) => {
  if (isSessionPrep) {
    return 'member_session_prep_detail';
  }
  if (viewerContext === 'coach') {
    return 'coach_reflection_detail';
  }
  return 'member_reflection_detail';
};

const getLoadErrorMessage = (
  isSessionPrep: boolean,
  status: string,
  error: unknown,
) => {
  if (status !== 'error') {
    return undefined;
  }
  const label = isSessionPrep ? 'session prep' : 'reflection';
  const details = getLoadErrorDetails(error);
  if (isDriveConnectRequired(details)) {
    return `To open this ${label}, connect Google Drive in Moments, then reopen this link.`;
  }
  const code = getLoadErrorCode(details);
  return [
    `Failed to load ${label}.`,
    getLoadErrorReason(details.error_status),
    code ? `(Error: ${code})` : undefined,
  ]
    .filter(Boolean)
    .join(' ');
};

// Drive is connected from the Moments integrations page: it shows the Connect
// action when Moments restores a stored session, and Moments sign-in sends a
// person without one straight to Google consent.
const getLoadErrorAction = (
  status: string,
  error: unknown,
  momentsBaseUrl: string | null,
) =>
  status === 'error' &&
  momentsBaseUrl &&
  isDriveConnectRequired(getLoadErrorDetails(error))
    ? {
        href: `${momentsBaseUrl}/settings/integrations`,
        label: 'Connect Google Drive',
      }
    : undefined;

const getArtifactStatus = ({
  artifact,
  error,
  isError,
  isLoading,
}: {
  artifact: MomentsArtifactDetail | null | undefined;
  error: unknown;
  isError: boolean;
  isLoading: boolean;
}): MemberReflectionStatus => {
  if (artifact) {
    return 'ready';
  }
  if (isLoading) {
    return 'loading';
  }
  if (isError) {
    return isNotFoundOrAccessDenied(error) ? 'not-found' : 'error';
  }
  return 'not-found';
};

const getLatestFeedbackValue = (
  feedbackType: string | undefined,
): MemberReflectionFeedbackValue => {
  if (feedbackType === 'positive' || feedbackType === 'negative') {
    return feedbackType;
  }
  return null;
};

const mapArtifactNoteToHighlight = (
  note: MomentsArtifactNote,
): MemberReflectionArtifactHighlight => ({
  blockId: note.blockId,
  createdAt: note.createdAt,
  endIndex: note.endIndex,
  id: note.id,
  note: note.note,
  selectedText: note.selectedText,
  ...(note.sourceIdentity !== undefined
    ? { sourceIdentity: legacyStoredSourceIdentity(note.sourceIdentity) }
    : {}),
  startIndex: note.startIndex,
  updatedAt: note.updatedAt,
});

const getSourceReflectionLink = (
  artifact: MomentsArtifactDetail | null | undefined,
) => {
  const reflection = artifact?.sessionPrep?.sourceReflection;
  if (!reflection?.artifactId) {
    return undefined;
  }
  const routeDateSlug = reflection.routeDateSlug || 'reflection';
  const dateLabel =
    formatDateLabel(artifact?.sessionPrep?.sourcePlannedStartAt) ||
    formatDateLabel(routeDateSlug) ||
    'previous';
  return {
    href: `/m/artifacts/session-reflection/${reflection.artifactId}/${routeDateSlug}`,
    label: `Based on your ${dateLabel} session`,
  };
};

const getSingleLegacySessionId = (
  artifact: MomentsArtifactDetail | null | undefined,
): string | undefined => {
  const legacySessionIds = new Set(
    (artifact?.transcriptSessions ?? [])
      .map((session) => session.legacySessionId?.trim())
      .filter((id): id is string => Boolean(id)),
  );
  return legacySessionIds.size === 1
    ? Array.from(legacySessionIds)[0]
    : undefined;
};

const getChemistrySelectionCoach = (
  session:
    | {
        coach?: { firstName?: string | null } | null;
        coachId?: string | null;
      }
    | null
    | undefined,
) => ({
  coachFirstName: session?.coach?.firstName?.trim() ?? '',
  coachId: session?.coachId?.trim() ?? '',
});

const getSessionDateLabel = ({
  artifact,
  dateSlug,
}: {
  artifact: MomentsArtifactDetail | null | undefined;
  dateSlug: string | undefined;
}): string | undefined => {
  // The slug is the session's UTC day. The label is the viewer's local day of
  // the session start instant whose UTC day matches the slug (MEN-5433).
  const transcriptStarts = (artifact?.transcriptSessions ?? [])
    .map((session) => session.sessionStartAt)
    .filter((value): value is string => Boolean(value));
  const matchingStart = [
    artifact?.sessionPrep?.targetPlannedStartAt,
    ...transcriptStarts,
  ].find((value) => value && toDateSlug(value, '') === dateSlug);
  if (matchingStart) {
    return formatDateLabel(matchingStart);
  }
  return (
    (dateSlug === 'reflection' ? undefined : formatDateLabel(dateSlug)) ??
    formatDateLabel(transcriptStarts[0])
  );
};

const EMPTY_ARTIFACT_NOTES: MomentsArtifactNote[] = [];

const MemberReflectionArtifactPage = () => {
  const { artifactId = '', dateSlug } = useParams<{
    artifactId: string;
    dateSlug: string;
  }>();
  const location = useLocation();
  const [copyState, setCopyState] = useState<MemberReflectionCopyState>('idle');
  const [createFeedback, { isLoading: isFeedbackSubmitting }] =
    useCreateMomentsArtifactFeedbackMutation();
  const [dismissIntro, { isLoading: isDismissingIntro }] =
    useDismissDismissableViewMutation();
  const [createArtifactNote] = useCreateMomentsArtifactNoteMutation();
  const [updateArtifactNote, { isLoading: isArtifactNoteUpdating }] =
    useUpdateMomentsArtifactNoteMutation();
  const [deleteArtifactNote, { isLoading: isArtifactNoteDeleting }] =
    useDeleteMomentsArtifactNoteMutation();
  const [recordSurveyAnswer] = useRecordMomentsArtifactSurveyAnswerMutation();
  const viewerContext = location.pathname.startsWith('/coach/')
    ? 'coach'
    : 'member';
  const isSessionPrep = location.pathname.includes('/session-prep/');
  const feedbackSurface = getFeedbackSurface(isSessionPrep, viewerContext);
  const notesEnabled = viewerContext === 'member' && Boolean(artifactId.trim());
  const [isIntroDismissedLocally, setIsIntroDismissedLocally] = useState(false);
  const [introDismissError, setIntroDismissError] = useState<string>();
  const {
    currentData,
    isError: isCurrentUserError,
    isFetching: isCurrentUserFetching,
    isSuccess: isCurrentUserSuccess,
    refetchUser,
    user: currentUser,
  } = useCurrentUser();
  // Private notes only render for a member viewing their own reflection
  // (see notesEnabled above), so the note author is always the current user.
  const artifactNoteAuthor = useMemo(
    () =>
      notesEnabled
        ? {
            avatarUrl: currentUser.avatarUrl,
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
          }
        : undefined,
    [currentUser, notesEnabled],
  );

  const artifactArg = artifactId.trim() ? { artifactId } : skipToken;
  // Read `currentData`: after an in-app move to another artifact, `data` still
  // holds the previous artifact while the new one loads or fails, which would
  // render it under the new route and report a failed load as viewed.
  const {
    currentData: artifact,
    error: artifactError,
    isError: isArtifactError,
    isFetching: isArtifactFetching,
    isLoading: isArtifactLoading,
  } = useGetMomentsArtifactQuery(artifactArg);
  const keepSource =
    viewerContext === 'member' &&
    !isSessionPrep &&
    artifact?.id === artifactId &&
    artifact.profileSlug === MEMBER_REFLECTION_PROFILE_SLUG &&
    typeof artifact.revision === 'number'
      ? {
          id: artifact.id,
          ownerPersonId: artifact.ownerPersonId,
          profileSlug: artifact.profileSlug,
          revision: artifact.revision,
        }
      : undefined;
  const keep = useReflectionKeepCohort(keepSource);
  const showSavedNotesLink = useReflectionKeepFlag();
  const isMemberReflectionOnboardingEligible =
    viewerContext === 'member' &&
    !isSessionPrep &&
    artifact?.profileSlug === MEMBER_REFLECTION_PROFILE_SLUG;
  const chemistryLegacySessionId = useMemo(
    () => getSingleLegacySessionId(artifact),
    [artifact],
  );
  const canQueryChemistrySelectionSession =
    viewerContext === 'member' &&
    artifact?.profileSlug === CHEM_CALL_REFLECTION_MEMBER_PROFILE_SLUG &&
    Boolean(chemistryLegacySessionId);
  const chemistrySelectionSessionArg = canQueryChemistrySelectionSession
    ? chemistryLegacySessionId!
    : skipToken;
  const {
    currentData: chemistrySelectionSession,
    isFetching: isChemistrySelectionSessionFetching,
    isSuccess: isChemistrySelectionSessionSuccess,
  } = useGetSessionQuery(chemistrySelectionSessionArg);
  // `coachSelected` is Rails' answer to "has this member chosen a coach" (MEN-5348).
  // Coach count cannot stand in for it: a member still choosing can have one
  // candidate, and choosing leaves never-met candidates assigned.
  const {
    currentData: sessionsDashboard,
    isError: isSessionsDashboardError,
    isFetching: isSessionsDashboardFetching,
    isSuccess: isSessionsDashboardSuccess,
  } = useGetSessionsDashboardQuery(
    canQueryChemistrySelectionSession ? undefined : skipToken,
  );
  const {
    data: dismissableViewStates,
    isSuccess: isDismissableViewStatesLoaded,
  } = useGetDismissableViewStatesQuery(
    isMemberReflectionOnboardingEligible
      ? { keys: [MEMBER_REFLECTION_ONBOARDING_VIEW_KEY] }
      : skipToken,
  );
  const { data: latestFeedback } =
    useGetMomentsArtifactFeedbackLatestQuery(artifactArg);
  // Only the member who owns the artifact is surveyed, and only until their first answer.
  // During an in-app move to another artifact, the query still holds the previous
  // artifact, so require the loaded artifact to match the route before surveying.
  // Chemistry call reflections never carry the survey (MEN-5348), so the member's
  // one survey lands on a coaching reflection instead.
  const isSurveyEligible =
    viewerContext === 'member' &&
    artifact?.id === artifactId &&
    artifact.profileSlug !== CHEM_CALL_REFLECTION_MEMBER_PROFILE_SLUG;
  // Read `currentData` and `isSuccess`: while the query is skipped or after a failed
  // refetch, RTK Query still returns the last `data`, which may be a stale "unanswered".
  const {
    currentData: surveyStatus,
    isFetching: isSurveyStatusFetching,
    isSuccess: isSurveyStatusSuccess,
  } = useGetMomentsArtifactSurveyStatusQuery(
    isSurveyEligible ? undefined : skipToken,
  );
  // The first answer refetches the status as answered. Remember which artifact
  // the survey started on so it can finish there. Any other artifact, including
  // a return to this one, follows the settled status.
  const [surveyArtifactId, setSurveyArtifactId] = useState<string | null>(null);
  if (surveyArtifactId !== artifactId) {
    const nextSurveyArtifactId =
      surveyStatus?.answered === false &&
      isSurveyStatusSuccess &&
      !isSurveyStatusFetching
        ? artifactId
        : null;
    if (nextSurveyArtifactId !== surveyArtifactId) {
      setSurveyArtifactId(nextSurveyArtifactId);
    }
  }
  const showSurvey = isSurveyEligible && surveyArtifactId === artifactId;
  const { currentData: artifactNotesData } = useListMomentsArtifactNotesQuery(
    notesEnabled ? { artifactId } : skipToken,
  );

  const artifactHighlights = useMemo(
    () =>
      artifactNotesData?.notes
        .filter(
          (note) =>
            artifact?.id === artifactId && note.artifactId === artifactId,
        )
        .map((note) =>
          mapArtifactNoteToHighlight(
            reflectionNotePlacement(
              note,
              artifact?.id === artifactId &&
                typeof artifact.markdown === 'string' &&
                typeof artifact.revision === 'number'
                ? {
                    id: artifact.id,
                    markdown: artifact.markdown,
                    profileSlug: artifact.profileSlug,
                    revision: artifact.revision,
                  }
                : undefined,
              keep.enrolled,
            ),
          ),
        ) ?? [],
    [artifactNotesData?.notes, artifact, artifactId, keep.enrolled],
  );

  const status = getArtifactStatus({
    artifact,
    error: artifactError,
    isError: isArtifactError,
    isLoading: isArtifactLoading || isArtifactFetching,
  });

  useEffect(() => {
    if (status !== 'error') {
      return;
    }
    captureMessage(
      isSessionPrep
        ? 'member_session_prep_load_failed'
        : 'member_reflection_load_failed',
      {
        extra: { artifactId, ...getLoadErrorDetails(artifactError) },
        level: 'warning',
      },
    );
  }, [artifactError, artifactId, isSessionPrep, status]);

  // Mixpanel counterpart of the log above, so failed loads can be compared
  // with "Viewed artifact". Also covers "not found", which a member sees as a
  // dead link. Delivery goes through the Moments analytics session, so a
  // failure caused by an invalid Moments login may not be recorded here.
  useEffect(() => {
    if (
      viewerContext !== 'member' ||
      (status !== 'error' && status !== 'not-found')
    ) {
      return;
    }
    const loadErrorDetails = getLoadErrorDetails(artifactError);
    const errorStatus =
      loadErrorDetails.error_status === undefined
        ? undefined
        : String(loadErrorDetails.error_status);
    momentsAnalytics.track(
      MEMBER_MOMENTS_EVENTS.failedToLoadArtifact,
      {
        screen: isSessionPrep ? 'member_session_prep' : 'member_reflection',
        ...buildMemberMomentsAnalyticsProperties({
          artifactId,
          assetKind: isSessionPrep ? 'prep' : 'reflection',
          route: location.pathname,
          surface: 'artifact_detail',
        }),
        // error_message stays in the log only: it can hold a raw non-JSON
        // response body, and free text is kept out of Mixpanel.
        error_code: toAnalyticsCode(loadErrorDetails.error_code),
        error_status: errorStatus,
        failure_state: status,
        ms_since_page_load: Math.round(performance.now()),
        readiness_reason: toAnalyticsCode(loadErrorDetails.readiness_reason),
      },
      {
        dedupeKey: `artifact_load_failed:${artifactId}:${location.pathname}:${status}:${errorStatus}`,
      },
    );
  }, [
    artifactError,
    artifactId,
    isSessionPrep,
    location.pathname,
    status,
    viewerContext,
  ]);

  useEffect(() => {
    if (viewerContext !== 'member' || status !== 'ready' || !artifact) {
      return;
    }

    momentsAnalytics.viewed(
      MEMBER_MOMENTS_EVENTS.viewedArtifact,
      isSessionPrep ? 'member_session_prep' : 'member_reflection',
      location.pathname,
      buildMemberMomentsAnalyticsProperties({
        artifactId,
        profileSlug: artifact.profileSlug,
        route: location.pathname,
        sessionId: artifact.transcriptSessions[0]?.sessionId || undefined,
        surface: 'artifact_detail',
      }),
      { dedupeKey: `artifact:${artifactId}:${location.pathname}` },
    );
  }, [
    artifact,
    artifactId,
    isSessionPrep,
    location.pathname,
    status,
    viewerContext,
  ]);

  useEffect(() => {
    if (copyState === 'idle') {
      return undefined;
    }
    const timeout = window.setTimeout(() => setCopyState('idle'), 2000);
    return () => window.clearTimeout(timeout);
  }, [copyState]);

  const generationDateLabel = useMemo(
    () => formatDateLabel(artifact?.updatedAt),
    [artifact?.updatedAt],
  );
  const sessionDateLabel = useMemo(
    () => getSessionDateLabel({ artifact, dateSlug }),
    [artifact, dateSlug],
  );
  const feedbackValue = getLatestFeedbackValue(
    latestFeedback?.feedback?.feedbackType,
  );
  const isCurrentUserReady =
    isCurrentUserSuccess &&
    Boolean(currentData) &&
    !isCurrentUserError &&
    !isCurrentUserFetching;
  const coachSelectionCard = useMemo(() => {
    if (
      !canQueryChemistrySelectionSession ||
      !isCurrentUserReady ||
      !isChemistrySelectionSessionSuccess ||
      isChemistrySelectionSessionFetching ||
      resolveQueryAuthorityState({
        hasCurrentData: Boolean(sessionsDashboard),
        isError: isSessionsDashboardError,
        isFetching: isSessionsDashboardFetching,
        isSuccess: isSessionsDashboardSuccess,
      }) !== 'ready' ||
      sessionsDashboard?.coachSelected !== false
    ) {
      return undefined;
    }
    const session = chemistrySelectionSession as unknown;
    if (typeof session !== 'object' || session === null) {
      return undefined;
    }
    const currentSession = session as typeof chemistrySelectionSession;
    const { coachFirstName, coachId } =
      getChemistrySelectionCoach(currentSession);
    if (
      currentSession.userId !== currentData?.id ||
      !currentSession.isChemistryCall ||
      !coachId ||
      !coachFirstName ||
      !Array.isArray(currentData.coaches)
    ) {
      return undefined;
    }
    const matchedCoach = currentData.coaches.find(
      (coach) => coach.id === coachId,
    );
    if (!matchedCoach || !usesMomentsScheduling(matchedCoach)) {
      return undefined;
    }
    return {
      coachFirstName,
      href: `/m/book-biweekly/${encodeURIComponent(coachId)}`,
    };
  }, [
    canQueryChemistrySelectionSession,
    chemistrySelectionSession,
    currentData,
    isChemistrySelectionSessionFetching,
    isChemistrySelectionSessionSuccess,
    isCurrentUserReady,
    isSessionsDashboardError,
    isSessionsDashboardFetching,
    isSessionsDashboardSuccess,
    sessionsDashboard,
  ]);

  useEffect(() => {
    if (
      !canQueryChemistrySelectionSession ||
      typeof refetchUser !== 'function'
    ) {
      return;
    }
    try {
      Promise.resolve(refetchUser()).catch(() => undefined);
    } catch {
      // The current-user query can be skipped during auth bootstrap; retain the
      // fail-closed card until its existing query becomes refetchable.
    }
  }, [canQueryChemistrySelectionSession, refetchUser]);
  const artifactCopyText = artifact?.markdown.trim() ? artifact.markdown : '';
  const canCopyArtifact = status === 'ready' && Boolean(artifactCopyText);
  const transcriptHref = useMemo(() => {
    if (viewerContext !== 'member') {
      return null;
    }
    // The transcript route takes the Moments session id (`sessionId`), never the
    // Rails `legacySessionId` (MEN-4985).
    const transcriptSession = artifact?.transcriptSessions.find((session) =>
      session.sessionId?.trim(),
    );
    if (!transcriptSession?.sessionId) {
      return null;
    }
    return buildMemberSessionTranscriptPath({
      sessionDate: transcriptSession.sessionStartAt,
      sessionId: transcriptSession.sessionId,
    });
  }, [artifact?.transcriptSessions, viewerContext]);

  const handleBack = useBackNavigation(getBackFallbackPath(viewerContext));
  const navigate = useNavigate();

  const handleDismissIntro = useCallback(async () => {
    if (!isMemberReflectionOnboardingEligible) {
      return;
    }

    setIntroDismissError(undefined);
    try {
      await dismissIntro({
        key: MEMBER_REFLECTION_ONBOARDING_VIEW_KEY,
      }).unwrap();
      setIsIntroDismissedLocally(true);
    } catch {
      setIntroDismissError('Failed to dismiss this message. Please try again.');
    }
  }, [dismissIntro, isMemberReflectionOnboardingEligible]);

  const isIntroDismissedPersisted = Boolean(
    dismissableViewStates?.[MEMBER_REFLECTION_ONBOARDING_VIEW_KEY]?.dismissedAt,
  );
  const showIntroCallout =
    isMemberReflectionOnboardingEligible &&
    isDismissableViewStatesLoaded &&
    !isIntroDismissedPersisted &&
    !isIntroDismissedLocally;

  const handleCopy = useCallback(async () => {
    try {
      if (!artifactCopyText) {
        setCopyState('error');
        return;
      }
      await copyMarkdownToClipboard(artifactCopyText);
      momentsAnalytics.track(
        MEMBER_MOMENTS_EVENTS.copiedArtifact,
        buildMemberMomentsAnalyticsProperties({
          artifactId,
          copyKind: 'markdown',
          profileSlug: artifact?.profileSlug,
          route: location.pathname,
          surface: 'artifact_detail',
        }),
      );
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
  }, [artifact?.profileSlug, artifactId, location.pathname, artifactCopyText]);

  const handleFeedback = useCallback(
    (
      feedbackType: Exclude<MemberReflectionFeedbackValue, null>,
      feedbackText: string,
    ) => {
      if (!artifactId.trim()) {
        return Promise.resolve();
      }
      return createFeedback({
        artifactId,
        feedbackText: feedbackText || undefined,
        feedbackType,
        surface: feedbackSurface,
      })
        .unwrap()
        .then(() => {
          momentsAnalytics.track(
            MEMBER_MOMENTS_EVENTS.submittedArtifactFeedback,
            buildMemberMomentsAnalyticsProperties({
              artifactId,
              feedbackType,
              profileSlug: artifact?.profileSlug,
              route: location.pathname,
              surface: 'artifact_detail',
            }),
          );
        });
    },
    [
      artifact?.profileSlug,
      artifactId,
      createFeedback,
      feedbackSurface,
      location.pathname,
    ],
  );

  const handleSurveyAnswer = useCallback(
    async (question: MomentsArtifactSurveyQuestionId, answer: string) => {
      const result = await recordSurveyAnswer({
        answer,
        artifactId,
        question,
      }).unwrap();
      momentsAnalytics.track(
        MEMBER_MOMENTS_EVENTS.answeredArtifactSurvey,
        buildMemberMomentsAnalyticsProperties({
          artifactId,
          profileSlug: artifact?.profileSlug,
          route: location.pathname,
          surface: 'artifact_detail',
          surveyAnswer:
            question === 'stood_out'
              ? '<submitted>'
              : (answer as 'yes' | 'not_really' | 'no'),
          surveyId: result?.surveyId,
          surveyQuestion: question,
          surveyResponseId: result?.responseId,
        }),
      );
    },
    [artifact?.profileSlug, artifactId, location.pathname, recordSurveyAnswer],
  );

  const handleCreateArtifactNote = useCallback(
    async (selection: ArtifactTextSelection, note: string) => {
      if (!artifactId.trim() || viewerContext !== 'member') {
        return undefined;
      }

      const createdNote = await createArtifactNote({
        artifactId,
        authorRole: 'member',
        blockId: selection.blockId,
        endIndex: selection.endIndex,
        note,
        selectedText: selection.text,
        sourceIdentity: selection.sourceIdentity,
        startIndex: selection.startIndex,
        tags: [],
      }).unwrap();

      momentsAnalytics.track('Created artifact note', {
        artifact_id: artifactId,
        is_cross_block: selection.blockId.includes(':'),
        note_length: note.length,
        profile_slug: artifact?.profileSlug,
      });

      return createdNote ? mapArtifactNoteToHighlight(createdNote) : undefined;
    },
    [artifact?.profileSlug, artifactId, createArtifactNote, viewerContext],
  );

  const handleUpdateArtifactNote = useCallback(
    async (id: string, note: string) => {
      if (!artifactId.trim() || viewerContext !== 'member') {
        return;
      }

      await updateArtifactNote({ id, note }).unwrap();

      momentsAnalytics.track('Edited artifact note', {
        artifact_id: artifactId,
        note_id: id,
        note_length: note.length,
        profile_slug: artifact?.profileSlug,
      });
    },
    [artifact?.profileSlug, artifactId, updateArtifactNote, viewerContext],
  );

  const handleDeleteArtifactNote = useCallback(
    async (id: string) => {
      if (!artifactId.trim() || viewerContext !== 'member') {
        return;
      }

      await deleteArtifactNote({ id }).unwrap();

      momentsAnalytics.track('Deleted artifact note', {
        artifact_id: artifactId,
        note_id: id,
        profile_slug: artifact?.profileSlug,
      });
    },
    [artifact?.profileSlug, artifactId, deleteArtifactNote, viewerContext],
  );

  // Flagged v2 reflections (spec §4.1). The branch owns the flag check, so the
  // v1 path below never requests feature flags.
  if (
    viewerContext === 'member' &&
    !isSessionPrep &&
    artifact?.id === artifactId &&
    artifact.profileSlug === MEMBER_REFLECTION_V2_PROFILE_SLUG
  ) {
    return (
      <ReflectionV2Branch
        artifactId={artifactId}
        notes={artifactNotesData?.notes ?? EMPTY_ARTIFACT_NOTES}
        transcriptHref={transcriptHref}
        onBack={handleBack}
        onCreateNote={(arg) => createArtifactNote(arg).unwrap()}
        onDeleteNote={(id) => deleteArtifactNote({ id }).unwrap()}
        onFeedback={handleFeedback}
      />
    );
  }

  return (
    <MemberReflectionArtifactView
      key={`${artifactId.trim()}:${keep.generation}`}
      artifactHighlights={artifactHighlights}
      artifactNoteAuthor={artifactNoteAuthor}
      artifactNotesEnabled={notesEnabled}
      artifactTitle={
        artifact?.title ||
        (isSessionPrep
          ? 'Session prep'
          : memberReflectionLabel(artifact?.profileSlug ?? ''))
      }
      coachSelectionCard={coachSelectionCard}
      contentUnavailableMessage={
        isSessionPrep
          ? 'Session prep content is not available yet.'
          : 'Reflection content is not available yet.'
      }
      copyAriaLabel={
        isSessionPrep ? 'Copy session prep text' : 'Copy reflection text'
      }
      copyState={copyState}
      copyTooltipLabel={
        isSessionPrep ? 'Copy session prep text' : 'Copy reflection text'
      }
      errorAction={getLoadErrorAction(
        status,
        artifactError,
        resolveMomentsBaseUrl({
          envUrl: import.meta.env.VITE_MOMENTS_BASE_URL,
          fallbackUrl: currentUser.momentsBaseUrl,
        }),
      )}
      errorMessage={getLoadErrorMessage(isSessionPrep, status, artifactError)}
      feedbackValue={feedbackValue}
      generationDateLabel={generationDateLabel}
      introCopy={
        isSessionPrep
          ? 'Use this prep to choose where to begin in your next coaching conversation. It is based on your previous session, so you can reconnect with what mattered and bring a clear starting point.'
          : undefined
      }
      introDismissError={introDismissError}
      isArtifactNoteDeleting={isArtifactNoteDeleting}
      isArtifactNoteUpdating={isArtifactNoteUpdating}
      isDismissingIntro={isDismissingIntro}
      isFeedbackSubmitting={isFeedbackSubmitting}
      markdown={artifact?.markdown}
      participantLabel={undefined}
      reflectionKeep={
        viewerContext === 'member' &&
        !isSessionPrep &&
        (!artifact || artifact.profileSlug === MEMBER_REFLECTION_PROFILE_SLUG)
          ? { enabled: keep.enabled, revision: keepSource?.revision }
          : undefined
      }
      sessionDateLabel={sessionDateLabel}
      showIntroCallout={showIntroCallout}
      showSavedNotesLink={showSavedNotesLink}
      sourceReflectionLink={
        isSessionPrep ? getSourceReflectionLink(artifact) : undefined
      }
      status={status}
      statusCopy={
        isSessionPrep
          ? {
              error: 'Failed to load session prep.',
              loading: 'Loading session prep…',
              notFound: 'Session prep not found.',
            }
          : undefined
      }
      survey={
        showSurvey ? (
          <ArtifactSurvey key={artifactId} onAnswer={handleSurveyAnswer} />
        ) : undefined
      }
      transcriptHref={transcriptHref}
      viewerContext={viewerContext}
      onArtifactNoteCreate={notesEnabled ? handleCreateArtifactNote : undefined}
      onArtifactNoteDelete={notesEnabled ? handleDeleteArtifactNote : undefined}
      onArtifactNoteUpdate={notesEnabled ? handleUpdateArtifactNote : undefined}
      onBack={handleBack}
      onCopy={canCopyArtifact ? handleCopy : undefined}
      onDismissIntro={
        isMemberReflectionOnboardingEligible ? handleDismissIntro : undefined
      }
      onFeedback={handleFeedback}
      onManageSavedNotes={() => navigate('/m/highlights?view=owned-snapshots')}
    />
  );
};

export default MemberReflectionArtifactPage;
